import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  AppConfigurationResponse,
  EditAppConfigurationRequest,
  editAppConfiguration,
  getAllAppConfigurations,
} from 'core'

import { Button, Card, Modal, Notification, Table, TextAreaV2 } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

const AppConfigurations: React.FC = () => {
  const { t: translate } = useTranslation()

  const [editItem, setEditITem] = useState<AppConfigurationResponse | null>()

  const {
    data: configurations,
    isLoading: isAppConfigurationsLoading,
    reload: reloadAppConfigurations,
  } = useQueryRequestV2({
    queryFn: () => getAllAppConfigurations(),
    initialState: [],
  })

  const { sendCommand: sendEditAppConfiguration, isLoading: isCommandLoading } = useCommandRequestV2(
    (request: EditAppConfigurationRequest) => editAppConfiguration(request),
  )

  const formValidator = () =>
    Yup.object({
      value: Yup.string().required('Required'),
      key: Yup.string().required('Required'),
    })

  const columns: any[] = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: AppConfigurationResponse) => (
        <Button onClick={() => setEditITem(record)}>Edit</Button>
      ),
    },
  ]

  const onSubmit = async (values: AppConfigurationResponse) => {
    const result = await sendEditAppConfiguration(values)
    if (result.completed) {
      setEditITem(null)
      Notification.success('Configuration updated successfully')
      await reloadAppConfigurations()
    }
  }

  return (
    <>
      <Card title={translate('csi-dashboard.configurations.title')}>
        <Table dataSource={configurations} columns={columns} loading={isAppConfigurationsLoading} />
      </Card>

      <Modal
        width={'960px'}
        onCancel={() => setEditITem(null)}
        visible={!!editItem}
        title={'Edit configuration'}
        footer={null}
      >
        {editItem && (
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator()}
            initialValues={{ key: editItem.key, value: editItem.value }}
            onSubmit={(values) => onSubmit(values)}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <TextAreaV2 name={'value'} label={editItem.key} className={'mb-24'} rows={25} />

                <Button
                  className={'mr-8'}
                  htmlType={'submit'}
                  loading={isCommandLoading}
                  disabled={isCommandLoading || !formValidator().isValidSync(formik.values)}
                >
                  Save
                </Button>
              </form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  )
}

export default AppConfigurations
